import React, { useMemo, useState, useEffect } from "react";
import { ICrewBaseAssignment, ICrewBase, ICrewBaseEmbedded } from "@vmosolutions/oc-interfaces";
import { Formik, FormikProps, FormikHelpers } from "formik";
import { FormikSelectField, FormikDateTimeField, FormikTextField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Grid, Button, Modal, Paper, Box, ButtonGroup } from "@mui/material";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { CrewFormType } from "./CrewForm";
import _ from "lodash";
import * as Yup from "yup";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import moment from "moment-timezone";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { v1 } from "uuid";

interface ICrewBaseAssignmentProps {
  crewBases: ICrewBase[];
  crewForm: CrewFormType;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  showCrewBaseModal: boolean;
  setShowCrewBaseModal: any;
}

const initialCrewBaseAssignmentValues: ICrewBaseAssignment = {
  _id: undefined,
  assignedStart: undefined,
  base: undefined,
  oneWayCommuteTime: undefined,
};

type CrewBaseAssignmentForm = FormikProps<ICrewBaseAssignment>;
/**
 * Form to view/edit assigned Crew Bases.
 * @param props - Assigned Crew Bases.
 */
export const CrewBaseAssignmentField = (props: ICrewBaseAssignmentProps): JSX.Element => {
  const { crewBases, crewForm, setActiveModal, showCrewBaseModal, setShowCrewBaseModal } = props;
  const [assignedCBAs, setAssignedCBAs] = useState<ICrewBaseAssignment[]>([]);
  const [selectedCBA, setSelectedCBA] = useState<ICrewBaseAssignment | null>(null);
  useEffect(() => {
    setAssignedCBAs(crewForm?.values?.crewInfo?.bases ?? []);
  }, [crewForm]);

  const baseOptions: ICrewBaseEmbedded[] = useMemo(() => {
    return (
      crewBases?.map((crewBase) => ({
        baseRefId: crewBase._id,
        ICAO: crewBase.ICAO,
        timezoneId: crewBase.timezoneId,
        name: crewBase.name,
      })) ?? []
    );
  }, [crewBases]);

  const handleDelete = (cba: ICrewBaseAssignment): void => {
    setActiveModal({
      title: "Delete Crew Base Entry",
      message: "Please confirm that you want to delete this crew base",
      cancel: () => {
        setActiveModal(null);
      },
      confirm: (): Promise<void> => {
        setActiveModal(null);
        const newCBAs = [...assignedCBAs];
        const index = assignedCBAs.findIndex((assignedCBA) => assignedCBA._id === cba?._id);
        newCBAs.splice(index, 1);
        crewForm.setFieldValue("crewInfo.bases", [...newCBAs], true);
        crewForm.setFieldTouched("crewInfo.bases", true, true);
        setSelectedCBA(initialCrewBaseAssignmentValues);
        return null;
      },
      confirmLabel: "Confirm",
    });
  };

  const handleSubmit = (values: ICrewBaseAssignment, helpers: FormikHelpers<ICrewBaseAssignment>): void => {
    const { setStatus, resetForm } = helpers;
    const index = assignedCBAs.findIndex((cba) => cba._id === values?._id);
    if (index === -1) {
      const newValues = _.cloneDeep(assignedCBAs);
      values._id = `temp_${v1()}`;
      newValues.push(values);
      crewForm.setFieldValue("crewInfo.bases", [...newValues], true);
      crewForm.setFieldTouched("crewInfo.bases", true, false);
      resetForm({ values: initialCrewBaseAssignmentValues });
      setSelectedCBA(initialCrewBaseAssignmentValues);
    } else {
      const newValues = _.cloneDeep(assignedCBAs);
      newValues[index] = values;
      crewForm.setFieldValue("crewInfo.bases", [...newValues], true);
      crewForm.setFieldTouched("crewInfo.bases", true, false);
      resetForm({ values });
      setSelectedCBA(values);
    }
    setStatus({ success: true });
  };

  const CrewBaseAssignmentSchema = Yup.object().shape({
    assignedStart: Yup.number().required("Assigned Start date required").nullable(),
    base: Yup.object().required("Crew Base required").nullable(),
    oneWayCommuteTime: Yup.number()
      .required("Commute Time to airport required")
      .min(0, "Must exceed 0 minutes")
      .max(120, "Cannot exceed 120 minutes")
      .nullable(),
  });
  return (
    <>
      <Modal open={showCrewBaseModal}>
        <Paper elevation={4} sx={{ width: "75%", margin: "auto", marginTop: "10%", p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={6} sx={{ p: 1 }}>
              {assignedCBAs &&
                assignedCBAs.map((CBA: ICrewBaseAssignment, key: number) => {
                  return (
                    <DraggableListItem
                      key={key}
                      active={selectedCBA && CBA._id === selectedCBA?._id ? true : false}
                      onClick={() => setSelectedCBA(CBA)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Grid container item xs={12}>
                        <Grid container item xs={10} spacing={0}>
                          <Grid item xs={12} sx={{ fontSize: "1.1em", fontWeight: 900 }}>
                            {CBA.base.ICAO} ({CBA.base.name})
                          </Grid>
                          <Grid item xs={8} sx={{ fontSize: "0.8em" }}>
                            Start Date: {moment(CBA.assignedStart).tz("UTC").format()}
                          </Grid>
                          <Grid item xs={4} sx={{ fontSize: "0.9em", justifyContent: "end", display: "flex" }}>
                            Commute: {CBA.oneWayCommuteTime} mins
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          sx={{ fontSize: "0.9em", justifyContent: "end", alignItems: "center", display: "flex" }}
                        >
                          <EditIcon
                            fontSize="small"
                            sx={{ cursor: "pointer", mr: 1 }}
                            onClick={() => setSelectedCBA(CBA)}
                          />
                          <DeleteIcon
                            fontSize="small"
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              setSelectedCBA(CBA);
                              handleDelete(CBA);
                            }}
                          />
                        </Grid>
                      </Grid>
                    </DraggableListItem>
                  );
                })}
              <Button
                onClick={() => {
                  setSelectedCBA(initialCrewBaseAssignmentValues);
                }}
                variant="contained"
                color="success"
                size="small"
                fullWidth
              >
                <AddIcon fontSize="small" /> Add New Assignment
              </Button>
            </Grid>

            <Grid item xs={6} sx={{ p: 2 }}>
              <Formik
                initialValues={selectedCBA ?? initialCrewBaseAssignmentValues}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={CrewBaseAssignmentSchema}
              >
                {(subForm: CrewBaseAssignmentForm) => {
                  const { values, dirty, isValid, isSubmitting, handleReset } = subForm;

                  const mode = selectedCBA?._id ? "update" : "create";

                  return (
                    <Grid container direction="row" className="form_body">
                      <Grid item xs={12}>
                        <FormikDateTimeField
                          label="Assigned Start Date"
                          name="assignedStart"
                          hideTimeControl={true}
                          isEpoch={true}
                        />
                        <FormikSelectField
                          name="base"
                          label="Crew Base"
                          options={baseOptions}
                          getOptionLabel={(base: ICrewBaseEmbedded) => base.ICAO}
                          getOptionValue={(base: ICrewBaseEmbedded) => base.baseRefId}
                          placeholder="Select Crew Base..."
                          isClearable={true}
                          hideSelectedOptions={false}
                        />
                        <FormikTextField label="Commute Time (min)" name="oneWayCommuteTime" type="number" />
                      </Grid>
                      <Grid item xs={12} sx={{ pt: 1, display: "flex", justifyContent: "end" }}>
                        <ButtonGroup variant="contained" size="small">
                          <Button
                            type="reset"
                            color="warning"
                            disabled={(!dirty && isValid) || isSubmitting}
                            onClick={handleReset}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            disabled={isSubmitting || !isValid || !dirty}
                            onClick={() => handleSubmit(values, subForm)}
                            type="submit"
                          >
                            {mode === "update" ? "Modify Selected" : "Create New"}
                          </Button>
                        </ButtonGroup>
                      </Grid>
                    </Grid>
                  );
                }}
              </Formik>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", pt: 2, borderTop: "1px solid grey" }} justifyContent="end">
              <Button
                size="small"
                onClick={() => {
                  setShowCrewBaseModal(false);
                  setSelectedCBA(initialCrewBaseAssignmentValues);
                  crewForm.setFieldValue("crewInfo.bases", assignedCBAs, true);
                  crewForm.setFieldTouched("crewInfo.bases", true, true);
                }}
                variant="contained"
                color="error"
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
    </>
  );
};
